import React, { useEffect, useRef, useState } from 'react';
import { Search, SearchResult } from '@americanexpress/dls-react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { withRouter } from '@americanexpress/one-app-router';
import { getLocaleData } from '@/selectors/localeSelectors';
import { getBasePathFromLocation } from '@/helpers/utils';
import { useTranslatedAirportResults } from '@/hooks/airportHooks/airportsHelperHooks';
import { useRouteParams } from '@/context/routeParamsContext';
import {
  useOneCmsLocaleMinimumSearchLength,
  useOneCmsLocaleSearchObj,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useQueryParamsContext } from '@/context/queryParamsContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { removeFiltersQuery } from '@/helpers/airportFiltersHelper';
import LFLink from './LFLink';

import styles from './AirportsSearchBox.module.scss';

const generateCleanLabelForAirport = (airport) => {
  if (airport) {
    return `${airport.name} (${airport.iata}), ${airport.location.description}`;
  }
  return '';
};

const AirportSearchBox = ({ router, className }) => {
  const [query, setQuery] = useState('');
  const [routedAirport, setRoutedAirport] = useState('');
  const { product, airportCode: routedAirportCode } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const basePath = getBasePathFromLocation(locale, router.location.pathname);

  const { customTrack } = useAnalyticsContext();

  const inputRef = useRef(null);
  const queryParams = useQueryParamsContext();
  const airportsMatchingQuery = useTranslatedAirportResults(query);
  const airportsMatchingRoute = useTranslatedAirportResults(routedAirportCode);
  const minQueryLength = useOneCmsLocaleMinimumSearchLength();
  const searchCopy = useOneCmsLocaleSearchObj();
  useEffect(() => {
    // if there is a match for the iata, it will always be the first result
    if (airportsMatchingRoute.length > 0) {
      const airportMatchingRoute = airportsMatchingRoute[0];
      if (airportMatchingRoute?.iata === routedAirportCode) {
        setRoutedAirport(generateCleanLabelForAirport(airportMatchingRoute));
        setQuery(generateCleanLabelForAirport(airportMatchingRoute));
      }
    }
  }, [airportsMatchingRoute, routedAirportCode]);

  const onFocus = () => {
    if (query === routedAirport) {
      setQuery('');
    }
  };

  const onBlur = (e) => {
    if (query === '') {
      setQuery(routedAirport);
    }
    customTrack({ event: e, detail: query });
  };

  const handleOnChange = (event) => {
    setQuery(event.target.value);
  };

  return (
    <div className={className}>
      <Search
        id="findLounges"
        onBlur={onBlur}
        onFocus={onFocus}
        onClearSearch={() => setQuery('')}
        value={query || ''}
        containerRef={inputRef}
        // reactlytics={{ element: 'search bar', detail: query }}
        onChange={handleOnChange}
        getFilteredResults={() => {
          return (
            airportsMatchingQuery?.map((airport) => (
              <SearchResult
                key={airport?.id}
                id={airport?.iata}
                className={styles.searchResult}
              >
                <LFLink
                  key={`search-result-${airport?.iata}`}
                  detail={`search-result-${airport?.iata}`}
                  id={airport?.iata}
                  to={`${product}/${airport?.iata}/`}
                  tabIndex="-1"
                  className="pad-1 display-block link-underlined"
                  element="search-result"
                >
                  {generateCleanLabelForAirport(airport)}
                </LFLink>
              </SearchResult>
            )) || []
          );
        }}
        isOpen={query?.length >= minQueryLength && query !== routedAirport}
        onResultSelect={(event, eventId) => {
          const selectedAirport = airportsMatchingQuery.find(
            ({ iata } = {}) => iata === eventId
          );
          setRoutedAirport(generateCleanLabelForAirport(selectedAirport));
          setQuery(generateCleanLabelForAirport(selectedAirport));
          router.push(
            `${basePath}/${product}/${selectedAirport.iata}${removeFiltersQuery(queryParams)}`
          );
        }}
        placeholder={searchCopy?.placeholder}
      />
    </div>
  );
};

AirportSearchBox.propTypes = {
  className: PropTypes.string,
};

const hocChain = compose(withRouter);

export default hocChain(AirportSearchBox);
